/**
 * Format credit amount for display
 * Removes unnecessary .00 decimals while preserving meaningful decimal places
 * 
 * @param {number|string} credits - The credit amount to format
 * @returns {string} - Formatted credit amount
 * 
 * @example
 * formatCredits(2037.00) // '2037'
 * formatCredits(2037.25) // '2037.25'
 * formatCredits(2037.5)  // '2037.50'
 * formatCredits('2037.00') // '2037'
 */
export const formatCredits = (credits) => {
  if (credits === null || credits === undefined || credits === '') {
    return '0';
  }

  const numericCredits = typeof credits === 'string' ? parseFloat(credits) : credits;
  
  if (isNaN(numericCredits)) {
    return '0';
  }

  // Convert to number and check if it's a whole number
  const formatted = Number(numericCredits);
  
  // If it's a whole number, return without decimals
  if (formatted % 1 === 0) {
    return formatted.toString();
  }
  
  // If it has decimals, return with two decimal places
  return formatted.toFixed(2);
};

/**
 * Format credit amount with currency or unit suffix
 * 
 * @param {number|string} credits - The credit amount to format
 * @param {string} suffix - Optional suffix (e.g., 'credits', 'days')
 * @returns {string} - Formatted credit amount with suffix
 * 
 * @example
 * formatCreditsWithSuffix(2037.00, 'credits') // '2037 credits'
 * formatCreditsWithSuffix(2037.25, 'days') // '2037.25 days'
 */
export const formatCreditsWithSuffix = (credits, suffix = 'credits') => {
  const formatted = formatCredits(credits);
  return `${formatted} ${suffix}`;
}; 